import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import usePageMetadata from '../../hooks/usePageMetadata';
import useAuth from '../../auth/useAuth';
import {
  createMaterialRequest,
  deleteMaterialRequest,
  getMaterialRequests,
} from '../../services/materialRequestService';
import './MaterialRequests.css';

const MAX_REQUESTS = 50;
const MAX_MATERIAL_NAME_LENGTH = 60;

const formatRequestDate = (createdAt) => {
  if (!createdAt) {
    return '';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(createdAt));
};

const getErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message || fallbackMessage;

const MaterialRequests = () => {
  usePageMetadata(
    'Request a Material for ASiJoLab',
    'Signed-in ASiJoLab users can request an engineering material for review and possible inclusion in the material database.'
  );

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const canAccessRequests = Boolean(user);
  const isAdmin = user?.role === 'admin';
  const [materialName, setMaterialName] = useState('');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deletingRequestId, setDeletingRequestId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const loadRequests = useCallback(async () => {
    if (!canAccessRequests) {
      setRequests([]);
      setLoading(false);
      return;
    }

    setErrorMessage('');
    setLoading(true);

    try {
      const loadedRequests = await getMaterialRequests();
      const newestRequests = [...loadedRequests]
        .sort((firstRequest, secondRequest) => (
          new Date(secondRequest.createdAt) - new Date(firstRequest.createdAt)
        ))
        .slice(0, MAX_REQUESTS);

      setRequests(newestRequests);
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, 'Material requests could not be loaded.')
      );
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [canAccessRequests]);

  useEffect(() => {
    if (!authLoading) {
      loadRequests();
    }
  }, [authLoading, loadRequests]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    const trimmedMaterialName = materialName.trim();

    if (
      trimmedMaterialName.length < 1 ||
      trimmedMaterialName.length > MAX_MATERIAL_NAME_LENGTH
    ) {
      setErrorMessage('Material name must be between 1 and 60 characters.');
      return;
    }

    setSubmitting(true);

    try {
      await createMaterialRequest({ materialName: trimmedMaterialName });
      setMaterialName('');
      await loadRequests();
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, 'Material request could not be submitted.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    setErrorMessage('');
    setDeletingRequestId(requestId);

    try {
      await deleteMaterialRequest(requestId);
      await loadRequests();
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, 'Material request could not be deleted.')
      );
    } finally {
      setDeletingRequestId(null);
    }
  };

  if (authLoading) {
    return (
      <section className="materialRequestsPage">
        <h1>Request a Material</h1>
        <p className="materialRequestsStatus">Loading...</p>
      </section>
    );
  }

  if (!canAccessRequests) {
    return (
      <section className="materialRequestsPage">
        <h1>Request a Material</h1>
        <div className="materialRequestAccessMessage" role="alert">
          <h2>Sign in required</h2>
          <p>Request Material is available to every signed-in ASiJoLab user.</p>
          <button type="button" onClick={() => navigate('/login')}>
            Sign In
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="materialRequestsPage">
      <h1>Request a Material</h1>
      <p className="materialRequestIntro">
        Suggest an engineering material for review and possible inclusion in
        the ASiJoLab material database.
      </p>

      <form
        className="materialRequestForm"
        onSubmit={handleSubmit}
        aria-busy={submitting}
      >
        <label htmlFor="material-request-name">Material name</label>
        <div className="materialRequestInputRow">
          <input
            id="material-request-name"
            type="text"
            value={materialName}
            onChange={(event) => setMaterialName(event.target.value)}
            maxLength={MAX_MATERIAL_NAME_LENGTH}
            disabled={submitting}
            required
          />
          <button type="submit" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </form>

      {errorMessage && (
        <p className="materialRequestError" role="alert">
          {errorMessage}
        </p>
      )}

      <div className="materialRequestsListHeader">
        <h2>Latest Requests ({requests.length})</h2>
      </div>

      {loading ? (
        <p className="materialRequestsStatus">Loading...</p>
      ) : (
        <div className="materialRequestsList">
          {requests.map((request) => (
            <div className="materialRequestItem" key={request.id}>
              <div className="materialRequestMainLine">
                <strong>{request.materialName}</strong>
                {isAdmin && (
                  <button
                    type="button"
                    disabled={deletingRequestId === request.id}
                    onClick={() => handleDeleteRequest(request.id)}
                  >
                    {deletingRequestId === request.id ? 'Deleting...' : 'Delete'}
                  </button>
                )}
              </div>
              <div className="materialRequestMetaLine">
                {request.status || 'Pending'} &bull; {formatRequestDate(request.createdAt)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default MaterialRequests;
